import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  MESSAGE_MAX_LENGTH,
  dayLabel,
  isSameDay,
  msUntilNextLocalDay,
  normalizeMessageText,
  type MessageDoc,
} from "radio-shared";
import { api } from "../api";

/** Marge sous laquelle on considère que le lecteur est resté en bas du fil. */
const AT_BOTTOM_SLACK_PX = 40;

const hhmm = (ms: number) =>
  new Date(ms).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

/**
 * Le chat de la radio. Les messages arrivent par Firestore en temps réel ; on ne
 * fait que les afficher et poster les nouveaux par l'API.
 */
export function Chat({ messages, myUid }: { messages: MessageDoc[]; myUid: string }) {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * L'heure du jour, relue à chaque minuit local : sans ça, un onglet ouvert toute
   * la nuit — l'usage même de la radio — continuerait d'annoncer « Aujourd'hui »
   * au-dessus des messages de la veille jusqu'au prochain message.
   */
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setTimeout(() => setNowMs(Date.now()), msUntilNextLocalDay(nowMs));
    return () => clearTimeout(id);
  }, [nowMs]);

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** false dès que le lecteur remonte lire : on cesse alors de le ramener en bas. */
  const stickToBottom = useRef(true);
  /** true le temps d'un envoi : on rendra la main au champ juste après. */
  const refocusAfterSend = useRef(false);

  /**
   * Suivre l'arrivée des messages, sans jamais arracher le fil des mains de
   * quelqu'un qui est remonté lire. `useLayoutEffect` pour recaler avant que le
   * navigateur ne peigne, sinon le saut se voit.
   */
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottom.current = distance < AT_BOTTOM_SLACK_PX;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  /**
   * Enchaîner les messages sans repasser par la souris : désactiver le champ
   * pendant l'envoi lui fait perdre le focus, et le clic sur « Envoyer » le
   * déplace de toute façon. On le rend donc au champ dès qu'il est réactivé —
   * seulement après un envoi, jamais au premier rendu.
   */
  useEffect(() => {
    if (sending || !refocusAfterSend.current) return;
    refocusAfterSend.current = false;
    inputRef.current?.focus();
  }, [sending]);

  const send = async () => {
    const text = normalizeMessageText(draft);
    if (!text || sending) return;

    refocusAfterSend.current = true;
    setSending(true);
    setError(null);
    try {
      await api.sendMessage(text);
      setDraft("");
      // on vient d'écrire : on veut évidemment voir son propre message
      stickToBottom.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Envoi raté");
    } finally {
      setSending(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await api.deleteMessage(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Suppression ratée");
    }
  };

  const remaining = MESSAGE_MAX_LENGTH - draft.length;

  return (
    <section className="chat">
      <h2 className="panel__title">Le chat</h2>

      <div className="chat__list" ref={listRef}>
        {messages.length === 0 ? (
          <p className="muted">Personne n'a rien dit. À toi.</p>
        ) : (
          messages.map((m, i) => {
            const previous = messages[i - 1];
            // le fil couvre jusqu'à trente jours : on annonce chaque changement de jour
            const opensADay = !previous || !isSameDay(m.sentAtMs, previous.sentAtMs);
            // messages consécutifs d'une même personne : on ne répète pas son nom,
            // sauf si minuit s'est glissé entre les deux
            const grouped =
              !opensADay &&
              previous?.authorUid === m.authorUid &&
              m.sentAtMs - previous.sentAtMs < 120_000;

            return (
              <Fragment key={m.id}>
                {opensADay && <div className="chat__day">{dayLabel(m.sentAtMs, nowMs)}</div>}
                <div className={`msg${grouped ? " msg--grouped" : ""}`}>
                  {!grouped && (
                    <div className="msg__head">
                      {m.authorPhoto ? (
                        <img className="avatar avatar--sm" src={m.authorPhoto} alt="" />
                      ) : (
                        <span className="avatar avatar--sm avatar--initial">
                          {m.authorName.slice(0, 1).toUpperCase()}
                        </span>
                      )}
                      <span className="msg__author">{m.authorName}</span>
                      <span className="msg__time">{hhmm(m.sentAtMs)}</span>
                    </div>
                  )}
                  <div className="msg__body">
                    <span className="msg__text">{m.text}</span>
                    {m.authorUid === myUid && (
                      <button
                        className="msg__remove"
                        type="button"
                        title="retirer mon message"
                        onClick={() => void remove(m.id)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </Fragment>
            );
          })
        )}
      </div>

      <form
        className="chat__form"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <input
          ref={inputRef}
          className="chat__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Dis quelque chose"
          maxLength={MESSAGE_MAX_LENGTH}
          disabled={sending}
        />
        <button className="btn btn--primary" type="submit" disabled={sending || !draft.trim()}>
          Envoyer
        </button>
      </form>

      {remaining < 80 && <p className="chat__hint">{remaining} caractères restants</p>}
      {error && <p className="chat__hint chat__hint--error">{error}</p>}
    </section>
  );
}
